/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import {useLayoutEffect, useState} from "react";
import {ScriptStatus} from "./ScriptStatus";
import {isModuleSource} from "./ModuleSource";
import {supportsModuleScripts} from "./supportsModuleScripts";
import {changed, ScriptMetadata, ScriptType} from "./ScriptMetadata";
import {useScriptTracker} from "./useScriptTracker";
import {ScriptDefinition} from "./ScriptDefinition";
import {ScriptBuilder} from "./ScriptBuilder";

type HTMLScriptElementFactory = (metadata: ScriptMetadata) => HTMLScriptElement;
type HTMLScriptElementInjector = (metadata: ScriptMetadata) => void;

export function useScript({
  id,
  source,
  async = true,
  defer = true
}: ScriptDefinition): ScriptStatus {
  const {monitor, statusOf} = useScriptTracker();
  const [status, setStatus] = useState<ScriptStatus>(ScriptStatus.LOADING);

  const createScript: HTMLScriptElementFactory = (
    metadata: ScriptMetadata
  ): HTMLScriptElement => {
    // prettier-ignore
    return ScriptBuilder.builder()
        .withMetadata(metadata)
        .withAsync(async)
        .withDefer(defer)
        .with(monitor)
        .build();
  };

  const injectScript: HTMLScriptElementInjector = (
    metadata: ScriptMetadata
  ): void => {
    let script = document.getElementById(metadata.id);
    // If the script has not been injected, do so now
    if (script === null) {
      script = createScript(metadata);
      console.info(`Injecting ${script.id}`);
      document.head.appendChild(script);
    }
    // If the script location has changed, remove it and then load the new source
    else if (changed(script, metadata)) {
      setStatus(ScriptStatus.LOADING);
      console.info(`Ejecting ${script.id}`);
      document.head.removeChild(script);
      script = createScript(metadata);
      console.info(`Injecting ${script.id}`);
      document.head.appendChild(script);
    }
    statusOf(script.id)?.then(setStatus);
  };

  useLayoutEffect(() => {
    console.info("useScript effect running");
    if (isModuleSource(source)) {
      // inject the esm script
      if (source.module && supportsModuleScripts()) {
        injectScript({
          id,
          source: source.module,
          type: ScriptType.MODULE
        });
      }
      // inject the fallback source if present
      else if (source.fallback) {
        injectScript({
          id,
          source: source.fallback,
          type: ScriptType.CLASSIC
        });
      }
    }
    // inject the classic script
    else {
      injectScript({
        id,
        source: source.classic,
        type: ScriptType.CLASSIC
      });
    }
  }, [id, source]);

  return status;
}
